import React, { useEffect, useState } from "react";
import { Paper, IconButton, Tooltip, Chip } from "@mui/material";
import MUIDataTable from "../../../components/LegacyMuiDataTable";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import Delete from "@mui/icons-material/Delete";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

import CustomToolbar from "../../../components/CustomToolbar";
import { deleteSource, getSources } from "../../../store/ducks/duckSource";
import NavBar from "../../../components/Navbar";
import useURL from "../../../hooks/useURL";
import { BorderLinearProgress } from "../../../components/LoadingComponent";
import ConfirmDelete from "../../../components/confirmDelete";
import { useStylesLayout } from "../../../styles/Layout";
import PublicIcon from "../../../icons/Public";
import ConfidentialIcon from "../../../icons/Confidential";
import HighlyConfidentialIcon from "../../../icons/HighlyConfidential";
import InternalUseIcon from "../../../icons/InternalUse";

import SourceForm from "./components/SourceForm";

const confidentialIcons = {
  0: { icon: () => <PublicIcon height={35} width={35} />, label: "Public" },
  1: {
    icon: () => <InternalUseIcon height={35} width={35} />,
    label: "Internal",
  },
  2: {
    icon: () => <ConfidentialIcon height={35} width={35} />,
    label: "Confidential",
  },
  3: {
    icon: () => <HighlyConfidentialIcon height={35} width={35} />,
    label: "Highly Confidential",
  },
};

const sourceInitial = {
  id: 0,
  name: "",
  URL: "",
  redirect: false,
  owners: [],
  iD_Platform: "",
  Platform: 0,
  confidentiality: 0,
};

const Source = () => {
  const data = useSelector((state) => state.source.data);
  const loading = useSelector((state) => state.source.isLoading);
  const { getParams, getVariables } = useURL();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStylesLayout();

  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const [source, setSource] = useState(sourceInitial);

  const [isEdit, setEdit] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  useEffect(() => {
    dispatch(getSources());
  }, [dispatch]);

  const handleClickOpen =
    (source, isEdit = true) =>
    (event) => {
      event.preventDefault();

      setSource(source);
      setEdit(isEdit);

      setOpen(true);
    };

  const handleClose = () => {
    setOpen(false);
  };
  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleConfirm = (source) => (event) => {
    event.preventDefault();
    setSource(source);

    setOpenConfirm(true);
  };

  const handleDelete = (event) => {
    event.preventDefault();
    let { id, name } = source;

    dispatch(deleteSource(id)).then(() =>
      enqueueSnackbar(`Source ${name} Deleted succesfully`, {
        variant: "success",
      }),
    );
    handleCloseConfirm();
  };

  const getSourceName = (id) => data.find((x) => x.id === id)?.name ?? "";

  const columns = [
    {
      name: "id",
      label: "Id",
      options: {
        filter: true,
        customFilterListOptions: {
          render: (v) => getSourceName(v),
        },
        filterOptions: {
          renderValue: (v) => getSourceName(v),
        },
      },
    },
    {
      name: "deleted",
      label: "Deleted",
      options: {
        filter: true,
        display: false,
        filterType: "dropdown",
        customFilterListOptions: { render: (v) => `Deleted: ${v}` },
        filterList: [false],
      },
    },
    {
      name: "url",
      label: "Url",
      options: {
        filter: true,
        display: false,
      },
    },
    {
      name: "platformId",
      label: "Platform Id",
      options: {
        filter: false,
        display: false,
      },
    },
    {
      name: "name",
      label: "Name",
      options: {
        filter: true,
      },
    },
    {
      name: "url",
      label: "Parameters",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          let url = data[dataIndex].url;

          if (url && url !== "") {
            let params = getParams(url);

            return (
              <>
                {params &&
                  params.map((p, i) => (
                    <Tooltip key={`chip-tooltip${p.key}-${i}`} title={`${p.key}`}>
                      <Chip key={`chip-Param-${p.key}-${i}`} label={`${p.value}`} size="small" />
                    </Tooltip>
                  ))}
              </>
            );
          }
        },
      },
    },
    {
      name: "url",
      label: "Variables",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          let url = data[dataIndex].url;

          if (url && url !== "") {
            let variables = getVariables(url);

            return (
              <>
                {variables &&
                  variables.map((v, i) => (
                    <Chip key={`chip-variable-${v}-${i}`} label={`${v}`} size="small" />
                  ))}
              </>
            );
          }
        },
      },
    },
    {
      name: "redirect",
      label: "Redirect",
      align: "center",
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          let rowData = data[dataIndex];

          return (
            <div className={classes.iconSource}>
              {rowData.redirect ? <CheckIcon /> : <RemoveIcon />}
            </div>
          );
        },
        filterType: "dropdown",
        customFilterListOptions: { render: (v) => `Redirect: ${v}` },
      },
    },
    {
      name: "confidentality",
      label: "Confidentality",
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          let rowData = data[dataIndex];
          const { icon: Icon, label } = confidentialIcons[rowData.confidentiality];

          return (
            <Tooltip key={`confidentiality-${dataIndex}-${label}`} title={label}>
              <div className={classes.iconSource}>
                <Icon key={`icon-${dataIndex}-${label}`} />
              </div>
            </Tooltip>
          );
        },
        filterType: "dropdown",
      },
    },
    {
      name: "",
      label: "",
      options: {
        filter: false,
        sort: false,
        empty: true,
        viewColumns: false,
        customBodyRenderLite: (dataIndex) => {
          let rowData = data[dataIndex];

          return (
            <div style={{ maxWidth: 250 }}>
              <Tooltip title={"Edit"}>
                <IconButton aria-label="edit" size="small" onClick={handleClickOpen(rowData, true)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={rowData.deleted ? "Is already removed" : "Delete"}>
                <IconButton
                  aria-label="delete"
                  disabled={rowData.deleted}
                  size="small"
                  onClick={handleConfirm(rowData)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
        setCellProps: () => ({ style: { width: 130, textAlign: "right" } }),
      },
    },
  ];

  const options = {
    filterType: "multiselect",
    expandableRowsHeader: false,
    selectableRows: "none",
    download: false,
    print: false,
    viewColumns: true,
    customToolbar: () => {
      return (
        <CustomToolbar
          handleClick={handleClickOpen(sourceInitial)}
          handleReload={() => dispatch(getSources())}
        />
      );
    },
    //Dense
    setTableProps: () => {
      return {
        padding: "default",
        size: "small",
      };
    },
    textLabels: {
      body: {
        noMatch: loading ? "loading..." : "Sorry, no matching records found",
      },
    },
    setRowProps: (row, dataIndex, _rowIndex) => {
      let deleted = data[dataIndex].deleted;

      return (
        deleted && {
          style: { backgroundColor: "Gainsboro" },
        }
      );
    },
    responsive: "standard",
    fixedHeader: true,
    fixedSelectColumn: false,
    tableBodyHeight: "420px",
    jumpToPage: true,
  };

  return (
    <>
      <NavBar />
      <Paper className={classes.administrationPaper}>
        {loading && <BorderLinearProgress />}
        <ConfirmDelete
          handleClose={handleCloseConfirm}
          handleDelete={handleDelete}
          message={`Are you sure to Delete the Source with name: ${source.name}?`}
          open={openConfirm}
        />
        {open && source && (
          <SourceForm
            handleClose={handleClose}
            initialValues={source}
            isEdit={isEdit}
            open={open}
          />
        )}
        <MUIDataTable columns={columns} data={data} options={options} title={"Source"} />
      </Paper>
    </>
  );
};

export default Source;
